import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";
import { AlertTriangle, Users } from "lucide-react";

import Button from "../../../../shared/ui/button/Button";
import ConfirmDialog from "../../../../shared/ui/modal/ConfirmDialog";
import PageMeta from "../../../../shared/ui/common/PageMeta";
import PageBreadCrumb from "../../../../shared/ui/common/PageBreadCrumb";
import CenterAdminShell from "../components/CenterAdminShell";
import { useCenter, useSetToolPolicy } from "../../../center";
import {
    ANNUAL_DISCOUNT_PERCENT,
    CancelSubscriptionDialog,
    CouponField,
    PaymentIssueBanner,
    MAX_SEAT_LINE,
    SEAT_LINE,
    addableCenterModules,
    centerQuoteFor,
    checkoutErrorKey,
    lineLabel,
    modulePriceFor,
    payableWithCoupon,
    seatPreview,
    useCancelCenterSubscription,
    useCenterSubscription,
    useChangeCenterTier,
    useCheckCenterCoupon,
    useOpenCenterBillingPortal,
    useResumeCenterSubscription,
    useSetCenterSeats,
    useStartCenterCheckout,
} from "../../../subscription";
import type { BillingPeriod, Coupon, ModuleOption, PlanTier } from "../../../subscription";

const TIERS: PlanTier[] = ["plus", "max"];
import { moduleName } from "../../../marketplace";

const money = (cents: number, currency: string, language: string): string =>
    new Intl.NumberFormat(language, { style: "currency", currency: currency.toUpperCase() })
        .format(cents / 100);

const day = (date: Date, language: string): string =>
    new Intl.DateTimeFormat(language, { dateStyle: "long" }).format(date);

/**
 * The centre's seats: how many it pays for, and what that costs.
 *
 * The screen has one job beyond showing the bill, and it is the part people
 * get wrong: making clear that **hiring somebody does not change the bill**.
 * The centre pays for capacity. Only the number on this page moves money.
 */
export default function CenterSubscriptionPage() {
    const { t, i18n } = useTranslation();
    const language = i18n.language || "es";
    const { center } = useCenter();
    const centerId = center?.id;

    const { data: state, isLoading, isError, refetch } = useCenterSubscription(centerId);
    const checkout = useStartCenterCheckout(centerId);
    const checkCoupon = useCheckCenterCoupon(centerId);
    const setSeats = useSetCenterSeats(centerId);
    const cancel = useCancelCenterSubscription(centerId);
    const resume = useResumeCenterSubscription(centerId);
    const portal = useOpenCenterBillingPortal(centerId);
    const changeTier = useChangeCenterTier(centerId);
    // Plus or Max for every seat being bought, and the size being moved to
    // while its confirmation is open.
    const [tier, setTier] = useState<PlanTier>("plus");
    const [confirmingTier, setConfirmingTier] = useState<PlanTier | null>(null);

    const [period, setPeriod] = useState<BillingPeriod>("annual");
    const [draftSeats, setDraftSeats] = useState<number | null>(null);
    const [confirmingCancel, setConfirmingCancel] = useState(false);
    // Travels with the purchase; the total here stays undiscounted, because
    // what a discount comes to is the provider's arithmetic.
    const [coupon, setCoupon] = useState<Coupon | null>(null);
    const [chosenModules, setChosenModules] = useState<string[]>([]);
    // A module being added to a subscription that already exists: it is a
    // policy decision, not a purchase, and the provider prorates it.
    const [adding, setAdding] = useState<ModuleOption | null>(null);
    const [confirmingSeats, setConfirmingSeats] = useState(false);
    const setPolicy = useSetToolPolicy(centerId);
    const [searchParams, setSearchParams] = useSearchParams();
    const [banner, setBanner] = useState<"success" | "cancelled" | null>(null);

    useEffect(() => {
        const outcome = searchParams.get("checkout");
        if (outcome !== "success" && outcome !== "cancelled") return;
        setBanner(outcome);
        const next = new URLSearchParams(searchParams);
        next.delete("checkout");
        setSearchParams(next, { replace: true });
    }, [searchParams, setSearchParams]);

    if (isLoading) {
        return (
            <section aria-busy="true" className="p-6">
                <p className="text-sm text-content-tertiary">
                    {t("center.subscription.loading")}
                </p>
            </section>
        );
    }

    if (isError || !state) {
        return (
            <section className="p-6">
                <p role="alert" className="mb-3 text-sm text-red-600 dark:text-red-400">
                    {t("center.subscription.loadError")}
                </p>
                <Button variant="outline" onClick={() => void refetch()}>
                    {t("center.subscription.retry")}
                </Button>
            </section>
        );
    }

    const selected = centerQuoteFor(state, period, tier);
    const currency = "eur";
    const subscription = state.subscription;
    const cancelling = Boolean(subscription?.cancelAtPeriodEnd);
    // The end of the period that has been paid for: the day it renews, or
    // the day it stops when a cancellation is pending.
    const renewsOn = subscription?.currentPeriodEnd ?? null;

    // Buying and changing are the same number and two different acts. While
    // there is nothing to change yet, how many seats to buy is **part of the
    // purchase**: the field starts empty and there is nothing to buy until a
    // number is in it, because the seats a centre was created with are a demo
    // rather than a decision anybody made.
    const buying = subscription === null;
    const chosen = buying ? draftSeats : draftSeats ?? state.seats;
    const seats = chosen ?? 0;
    // The server refuses anything lower, and says why. Stopping it here means
    // the administrator finds out before pressing rather than after.
    const tooFew = chosen !== null && chosen < state.seatsUsed;
    // Zero is a legitimate answer to how many seats a centre wants, and it
    // buys nothing: a checkout for no seat would be a zero invoice.
    const nothingToBuy = buying && chosen === 0;
    // The cost of the number in the field, which the server has not been told
    // yet. Every line of a centre's quote is per seat, so the chosen number is
    // the quantity of all of them.
    // Capacity that has actually been paid for. A centre that never pays has
    // it by standing rather than by purchase.
    // Seats being added to a subscription that already exists. Money leaves
    // today for these; taking seats away is the other direction and costs
    // nothing, so only this one is confirmed.
    const addingSeats = subscription ? Math.max(seats - state.seats, 0) : 0;
    // The period this centre is actually billed in — not the one the radio
    // buttons happen to be showing. Anything added to a subscription that is
    // already running is charged the way that subscription is: a yearly
    // centre pays a module's yearly price per seat.
    const livePeriod = subscription?.billingPeriod ?? period;
    // The quote this subscription actually renews on. What the summary shows
    // once there is something to renew: the radio buttons are about a period
    // being chosen, and a monthly centre reading an annual total would be
    // reading somebody else's bill.
    const live = state.quotes.find((quote) => quote.period === livePeriod) ?? null;
    // What one seat costs over the period this subscription actually renews
    // on — not the one the radio buttons happen to be showing.
    // **What a seat actually costs this centre**: the seat and every paid
    // module it has, because a centre's modules are priced per seat too. A
    // new seat comes with the tools the centre already pays for, and the
    // invoice charges them across it — so a dialog quoting the bare seat was
    // quoting less than the card would see.
    const perSeatCents = (live?.lines ?? []).reduce((total, line) => total + line.unitCents, 0);
    // Whether anything rides along with the seat, which decides whether the
    // confirmation says so.
    const seatCarriesTools = (live?.lines ?? []).some(
        (line) => line.key !== SEAT_LINE && line.key !== MAX_SEAT_LINE
    );
    const bought = state.earlyAdopter || subscription !== null;
    // What the centre can add to this purchase, and what it has ticked. The
    // preview below prices them the same way it prices a seat: per seat.
    const offeredModules = addableCenterModules(state);
    // What is being bought, not what is held: the seats in the field and the
    // modules that are ticked.
    const chosenModuleOptions = offeredModules.filter((module) =>
        chosenModules.includes(module.slug)
    );
    // **What the centre pays, not what it is typing.** While it is buying,
    // the number in the field is the purchase, so the summary is a preview of
    // it. Once there is a subscription, the summary is what renews — it moves
    // when the money moves, and not before. What a change is about to cost is
    // said in the confirmation, broken down line by line.
    const shown = buying
        ? selected && seatPreview(selected, seats, chosenModuleOptions, period)
        : live;
    // Said only when something actually rides along. A sentence about tools
    // on a centre that has none would be noise in the one dialog that has to
    // be read.
    const toolsNote = seatCarriesTools
        ? ` ${t("center.subscription.seats.payConfirm.tools")}`
        : "";
    const toggleModule = (slug: string) =>
        setChosenModules((current) =>
            current.includes(slug)
                ? current.filter((other) => other !== slug)
                : [...current, slug]
        );
    // What the code leaves to pay, by the rule the provider bills with. Null
    // where it cannot be said — no code, or one tied to particular prices —
    // and then the field's own note says the discount lands at payment.
    const discounted = shown
        ? payableWithCoupon(shown.lines.map((line) => line.totalCents), coupon)
        : null;

    /* Inside the centre's own tabs once there is a centre to move around in.
       Before the seats are bought there is nothing to move to — the gate
       sends every other route back here — so the purchase stands alone
       rather than offering a row of tabs that bounce. */
    const body = (
        <div className="mx-auto max-w-3xl space-y-5 p-4">
            <PageMeta
                title={t("center.subscription.title")}
                description={t("center.subscription.description")}
            />
            <PageBreadCrumb pageTitle={t("center.subscription.title")} />

            {banner && (
                <p
                    role="status"
                    className={`rounded-lg p-3 text-sm ${
                        banner === "success"
                            ? "bg-green-50 text-green-800 dark:bg-green-500/10 dark:text-green-300"
                            : "bg-gray-50 text-content-secondary dark:bg-gray-800"
                    }`}
                >
                    {t(`center.subscription.checkout.${banner}`)}
                </p>
            )}

            {/* A declined renewal, while Stripe retries: the team keeps its
                plan, and the one thing to do is change the card. */}
            {subscription?.status === "past_due" && (
                <PaymentIssueBanner
                    message={t("subscription.billing.dunning.centerBody")}
                    onUpdateCard={() => portal.mutate(true)}
                    isBusy={portal.isPending}
                />
            )}
            {/* The next renewal is going to a card that will have expired. */}
            {subscription && subscription.status !== "past_due" && subscription.cardExpiring && (
                <PaymentIssueBanner
                    title={t("subscription.billing.cardExpiring.title")}
                    message={t("subscription.billing.cardExpiring.centerBody")}
                    onUpdateCard={() => portal.mutate(true)}
                    isBusy={portal.isPending}
                />
            )}

            {/* The window, when one is running. First, because it is the only
                thing on this page with a deadline. */}
            {state.graceEndsAt && (
                <div
                    role="alert"
                    className="flex items-start gap-3 rounded-xl bg-amber-50 p-4 dark:bg-amber-500/10"
                >
                    <AlertTriangle size={18} className="mt-0.5 shrink-0 text-amber-700 dark:text-amber-300" />
                    <p className="text-sm leading-relaxed text-amber-900 dark:text-amber-200">
                        {t("center.subscription.grace", {
                            date: day(state.graceEndsAt, language),
                        })}
                    </p>
                </div>
            )}

            {state.earlyAdopter ? (
                <section className="space-y-3 rounded-xl bg-brand-50 p-5 dark:bg-brand-500/10">
                    <h2 className="text-base font-semibold text-brand-700 dark:text-brand-300">
                        {t("center.subscription.earlyAdopter.title")}
                    </h2>
                    <p className="text-sm leading-relaxed text-content-secondary">
                        {t("center.subscription.earlyAdopter.body")}
                    </p>
                </section>
            ) : (
                <>
                    <section className="space-y-5 rounded-xl border border-gray-200 p-5 dark:border-gray-700">
                        <div className="flex items-center gap-2">
                            <Users size={18} className="text-content-tertiary" />
                            <h2 className="text-base font-semibold text-content-primary">
                                {t("center.subscription.seats.title")}
                            </h2>
                        </div>

                        <p className="text-sm text-content-secondary">
                            {/* "1 of 50 seats used" is a lie before anything is
                                paid for. What decides is the subscription, not
                                the number on the row: a centre that chose
                                fifty and never finished the checkout is not a
                                centre with fifty seats. */}
                            {bought
                                ? t("center.subscription.seats.usage", {
                                      used: state.seatsUsed,
                                      seats: state.seats,
                                  })
                                : t("center.subscription.seats.noneBought", {
                                      count: state.seatsUsed,
                                  })}
                        </p>

                        {/* The sentence this page exists for. */}
                        <p className="rounded-lg bg-gray-50 p-3 text-sm text-content-secondary dark:bg-gray-800">
                            {t("center.subscription.seats.capacityNote")}
                        </p>

                        <div className="flex flex-wrap items-end gap-3">
                            <label className="flex flex-col gap-1">
                                <span className="text-sm font-medium text-content-primary">
                                    {t(
                                        buying
                                            ? "center.subscription.seats.choose"
                                            : "center.subscription.seats.label"
                                    )}
                                </span>
                                <input
                                    type="number"
                                    min={state.seatsUsed}
                                    value={chosen ?? ""}
                                    aria-invalid={tooFew}
                                    onChange={(event) =>
                                        setDraftSeats(
                                            event.target.value === ""
                                                ? null
                                                : Number(event.target.value)
                                        )
                                    }
                                    className="w-28 rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-900"
                                />
                            </label>
                            {/* Nothing to save while nothing is bought: the
                                number goes with the purchase instead. */}
                            {!buying && (
                                <Button
                                    onClick={() =>
                                        addingSeats > 0
                                            ? setConfirmingSeats(true)
                                            : setSeats.mutate(seats)
                                    }
                                    disabled={
                                        tooFew ||
                                        seats === state.seats ||
                                        setSeats.isPending
                                    }
                                >
                                    {/* Buying seats is not saving a setting.
                                        The word on the button is what somebody
                                        reads before the card is charged. */}
                                    {addingSeats > 0
                                        ? t("center.subscription.seats.pay")
                                        : t("center.subscription.seats.save")}
                                </Button>
                            )}
                        </div>

                        {buying && chosen === null && (
                            <p className="text-sm text-content-secondary">
                                {t("center.subscription.seats.mustChoose")}
                            </p>
                        )}
                        {nothingToBuy && (
                            <p className="text-sm text-content-secondary">
                                {t("center.subscription.seats.noneToBuy")}
                            </p>
                        )}
                        {tooFew && (
                            <p role="alert" className="text-sm text-red-600 dark:text-red-400">
                                {t("center.subscription.seats.tooFew", {
                                    used: state.seatsUsed,
                                })}
                            </p>
                        )}
                        {setSeats.isError && (
                            <p role="alert" className="text-sm text-red-600 dark:text-red-400">
                                {t("center.subscription.seats.failed")}
                            </p>
                        )}
                        {/* Capacity released and still theirs: they paid for
                            it up to the renewal, so it is not taken away
                            before then. */}
                        {state.seatsAtRenewal !== null && (
                            <p className="rounded-lg bg-amber-50 p-3 text-sm text-amber-900 dark:bg-amber-500/10 dark:text-amber-200">
                                {renewsOn
                                    ? t("center.subscription.seats.droppingOn", {
                                          count: state.seatsAtRenewal,
                                          date: day(renewsOn, language),
                                      })
                                    : t("center.subscription.seats.dropping", {
                                          count: state.seatsAtRenewal,
                                      })}
                            </p>
                        )}

                        {/* What each direction costs, before the number is
                            changed rather than after the invoice arrives.
                            They are not symmetrical and that is the whole
                            point: one is charged now, the other is not
                            refunded. */}
                        {subscription && (
                            <div className="space-y-1 rounded-lg bg-gray-50 p-3 text-xs text-content-secondary dark:bg-gray-800">
                                <p>{t("center.subscription.seats.moreCosts")}</p>
                                <p>{t("center.subscription.seats.fewerCosts")}</p>
                            </div>
                        )}
                    </section>

                    {/* Modules the centre does not have, once it has a
                        subscription to put them on. Before that they are
                        bought with the seats, in the purchase below. */}
                    {subscription && offeredModules.length > 0 && (
                        <section className="space-y-4 rounded-xl border border-gray-200 p-5 dark:border-gray-700">
                            <h2 className="text-base font-semibold text-content-primary">
                                {t("center.subscription.modules.title")}
                            </h2>
                            <p className="text-sm text-content-secondary">
                                {t("center.subscription.modules.addHint")}
                            </p>
                            <ul className="space-y-2">
                                {offeredModules.map((module) => (
                                    <li
                                        key={module.slug}
                                        className="flex flex-wrap items-center gap-3 rounded-xl border border-gray-200 p-3 dark:border-gray-700"
                                    >
                                        <span className="text-sm font-medium text-content-primary">
                                            {moduleName(t, {
                                                slug: module.slug,
                                                name: module.name,
                                            })}
                                        </span>
                                        <span className="text-caption text-content-secondary">
                                            {/* Priced the way this centre is
                                                billed: adding one joins the
                                                subscription it already has. */}
                                            {t(
                                                livePeriod === "annual"
                                                    ? "center.subscription.modules.perSeatYear"
                                                    : "center.subscription.modules.perSeatMonth",
                                                {
                                                    price: money(
                                                        modulePriceFor(module, livePeriod),
                                                        currency,
                                                        language
                                                    ),
                                                }
                                            )}{" "}
                                            {t("subscription.billing.tax.suffix")}
                                        </span>
                                        <Button
                                            size="sm"
                                            className="ml-auto"
                                            onClick={() => setAdding(module)}
                                            disabled={setPolicy.isPending}
                                        >
                                            {t("center.subscription.modules.add")}
                                        </Button>
                                    </li>
                                ))}
                            </ul>
                        </section>
                    )}

                    <section className="space-y-4 rounded-xl border border-gray-200 p-5 dark:border-gray-700">
                        <div className="flex items-center justify-between gap-3">
                            <h2 className="text-base font-semibold text-content-primary">
                                {subscription
                                    ? t("center.subscription.cost.renewalTitle")
                                    : t("center.subscription.cost.title")}
                            </h2>
                            <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-800 dark:bg-green-500/15 dark:text-green-300">
                                {t("subscription.billing.annualSaving", {
                                    percent: ANNUAL_DISCOUNT_PERCENT,
                                })}
                            </span>
                        </div>

                        {/* Plus or Max for every seat: chosen with the
                            purchase, and moved later from here. */}
                        {buying ? (
                            <div
                                role="radiogroup"
                                aria-label={t("center.subscription.tier.legend")}
                                className="flex flex-col gap-3 sm:flex-row"
                            >
                                {TIERS.map((option) => {
                                    const seat = centerQuoteFor(state, period, option)?.lines[0];
                                    const perMonth =
                                        seat === undefined
                                            ? null
                                            : period === "annual"
                                              ? Math.floor(seat.unitCents / 12)
                                              : seat.unitCents;
                                    return (
                                        <label
                                            key={option}
                                            className={`flex flex-1 cursor-pointer items-start gap-3 rounded-xl border p-3 ${
                                                tier === option
                                                    ? "border-brand-500 bg-brand-50 dark:bg-brand-500/10"
                                                    : "border-gray-200 dark:border-gray-700"
                                            }`}
                                        >
                                            <input
                                                type="radio"
                                                name="center-tier"
                                                value={option}
                                                checked={tier === option}
                                                onChange={() => setTier(option)}
                                                className="mt-1 accent-brand-500"
                                            />
                                            <span className="flex flex-col gap-0.5 text-sm">
                                                <span className="font-semibold text-content-primary">
                                                    {t(`subscription.billing.tier.${option}.name`)}
                                                </span>
                                                <span className="text-content-secondary">
                                                    {t(`subscription.billing.tier.${option}.sessions`)}
                                                </span>
                                                {perMonth !== null && (
                                                    <span className="text-xs text-content-tertiary">
                                                        {t("center.subscription.tier.perSeat", {
                                                            price: money(perMonth, currency, language),
                                                        })}{" "}
                                                        {t("subscription.billing.tax.suffix")}
                                                    </span>
                                                )}
                                            </span>
                                        </label>
                                    );
                                })}
                            </div>
                        ) : (
                            subscription && (
                                <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-gray-50 p-3 text-sm dark:bg-gray-800">
                                    <div>
                                        <p className="font-semibold text-content-primary">
                                            {t("center.subscription.tier.current", {
                                                tier: t(
                                                    `subscription.billing.tier.${subscription.heldTier}.name`
                                                ),
                                            })}
                                            {" · "}
                                            <span className="font-normal text-content-secondary">
                                                {t(
                                                    `subscription.billing.tier.${subscription.heldTier}.sessions`
                                                )}
                                            </span>
                                        </p>
                                        {subscription.tier !== subscription.heldTier &&
                                            subscription.maxUntil && (
                                                <p className="text-xs text-content-tertiary">
                                                    {t("subscription.billing.tier.maxUntil", {
                                                        date: day(subscription.maxUntil, language),
                                                    })}
                                                </p>
                                            )}
                                    </div>
                                    {!cancelling && subscription.status !== "canceled" && (
                                        <Button
                                            variant="outline"
                                            disabled={changeTier.isPending}
                                            onClick={() =>
                                                setConfirmingTier(
                                                    subscription.tier === "max" ? "plus" : "max"
                                                )
                                            }
                                        >
                                            {t(
                                                subscription.tier === "max"
                                                    ? "subscription.billing.tier.toPlus"
                                                    : "subscription.billing.tier.toMax"
                                            )}
                                        </Button>
                                    )}
                                    {changeTier.isError && (
                                        <p role="alert" className="w-full text-sm text-red-600 dark:text-red-400">
                                            {t(checkoutErrorKey(changeTier.error))}
                                        </p>
                                    )}
                                </div>
                            )
                        )}

                        {/* Only while there is a period to choose. Once the
                            centre is paying, the period is a fact about its
                            subscription — changing it means a new checkout —
                            and radio buttons that moved the total without
                            moving the bill showed a monthly centre an annual
                            figure under the words "what you pay at each
                            renewal". */}
                        {buying && (
                        <div role="radiogroup" aria-label={t("subscription.billing.periodChoice")} className="flex gap-3">
                            {(["monthly", "annual"] as BillingPeriod[]).map((option) => (
                                <label
                                    key={option}
                                    className={`flex flex-1 cursor-pointer items-center gap-3 rounded-xl border p-3 ${
                                        period === option
                                            ? "border-brand-500 bg-brand-50 dark:bg-brand-500/10"
                                            : "border-gray-200 dark:border-gray-700"
                                    }`}
                                >
                                    <input
                                        type="radio"
                                        name="center-period"
                                        value={option}
                                        checked={period === option}
                                        onChange={() => setPeriod(option)}
                                        className="accent-brand-500"
                                    />
                                    <span className="text-sm font-medium text-content-primary">
                                        {t(`subscription.billing.period.${option}`)}
                                    </span>
                                </label>
                            ))}
                        </div>
                        )}

                        {/* The modules, bought with the seats and priced the
                            same way: per seat. A new centre cannot reach its
                            tools screen — nothing works until it pays — so
                            without this the only thing it could buy in one go
                            is bare capacity. */}
                        {buying && offeredModules.length > 0 && (
                            <fieldset className="space-y-3">
                                <legend className="text-sm font-semibold text-content-primary">
                                    {t("center.subscription.modules.title")}
                                </legend>
                                <p className="text-caption text-content-tertiary">
                                    {t("center.subscription.modules.hint")}
                                </p>
                                <div className="space-y-2">
                                    {offeredModules.map((module) => (
                                        <label
                                            key={module.slug}
                                            className={`flex cursor-pointer items-start gap-3 rounded-xl border p-3 ${
                                                chosenModules.includes(module.slug)
                                                    ? "border-brand-500 bg-brand-50 dark:bg-brand-500/10"
                                                    : "border-gray-200 dark:border-gray-700"
                                            }`}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={chosenModules.includes(module.slug)}
                                                disabled={checkout.isPending}
                                                onChange={() => toggleModule(module.slug)}
                                                className="mt-1 accent-brand-500"
                                            />
                                            <span className="flex flex-1 flex-wrap items-baseline justify-between gap-2">
                                                <span className="text-sm font-medium text-content-primary">
                                                    {moduleName(t, {
                                                        slug: module.slug,
                                                        name: module.name,
                                                    })}
                                                </span>
                                                <span className="text-caption text-content-secondary">
                                                    {t(
                                                        period === "annual"
                                                            ? "center.subscription.modules.perSeatYear"
                                                            : "center.subscription.modules.perSeatMonth",
                                                        {
                                                            price: money(
                                                                modulePriceFor(module, period),
                                                                currency,
                                                                language
                                                            ),
                                                        }
                                                    )}{" "}
                                                    {t("subscription.billing.tax.suffix")}
                                                </span>
                                            </span>
                                        </label>
                                    ))}
                                </div>
                            </fieldset>
                        )}

                        {/* What this total is: what renews, or what is about
                            to be charged. The same figure means two different
                            things on the two sides of a purchase, and only
                            one of them is money leaving today. */}
                        {shown && (
                            <div className="space-y-1">
                                {/* When, not only how much. A renewal date is
                                    the first thing anybody looks for on a
                                    subscription screen, and a cancellation
                                    turns that same date into the day it all
                                    stops. */}
                                {renewsOn && (
                                    <p className="text-sm font-medium text-content-primary">
                                        {t(
                                            cancelling
                                                ? "center.subscription.cost.endsOn"
                                                : "center.subscription.cost.renewsOn",
                                            { date: day(renewsOn, language) }
                                        )}
                                    </p>
                                )}
                                <p className="text-caption text-content-tertiary">
                                    {subscription
                                        ? t("center.subscription.cost.renewalNote")
                                        : t("center.subscription.cost.dueNowNote")}
                                </p>
                            </div>
                        )}

                        {shown && (
                            <div className="space-y-2">
                                {shown.lines.map((line) => (
                                    <div key={line.key} className="flex justify-between gap-4 text-sm">
                                        <span className="text-content-secondary">
                                            {/* The same naming the therapist's
                                                receipt uses: the seat in the
                                                reader's language, a module by
                                                the catalogue's own copy. */}
                                            {t("subscription.billing.lineWithQuantity", {
                                                label: lineLabel(t, line),
                                                quantity: line.quantity,
                                            })}
                                        </span>
                                        <span className="font-medium text-content-primary">
                                            {money(line.totalCents, currency, language)}
                                        </span>
                                    </div>
                                ))}
                                {discounted !== null && (
                                    /* The code's own line, so the total below
                                       it is arrived at rather than asserted. */
                                    <div className="flex justify-between gap-4 border-t border-gray-200 pt-2 text-sm text-green-700 dark:border-gray-700 dark:text-green-400">
                                        <span>
                                            {t("subscription.billing.couponLine", {
                                                code: coupon?.code,
                                            })}
                                        </span>
                                        <span className="font-medium">
                                            −{money(shown.totalCents - discounted, currency, language)}
                                        </span>
                                    </div>
                                )}
                                <div className="flex justify-between gap-4 border-t border-gray-200 pt-2 text-sm font-semibold text-content-primary dark:border-gray-700">
                                    <span>{t("subscription.billing.total")}</span>
                                    <span className="flex items-baseline gap-2">
                                        {discounted !== null && (
                                            /* The undiscounted figure stays,
                                               struck through: a total that
                                               simply dropped leaves somebody
                                               wondering what it was. */
                                            <span className="text-caption font-normal text-content-tertiary line-through">
                                                {money(shown.totalCents, currency, language)}
                                            </span>
                                        )}
                                        <span>
                                            {money(
                                                discounted ?? shown.totalCents,
                                                currency,
                                                language
                                            )}
                                        </span>
                                    </span>
                                </div>
                                {/* Before VAT, like every figure here: Stripe
                                    adds it at payment from the centre's
                                    address and VAT number. */}
                                <p className="text-xs text-content-tertiary">
                                    {t("subscription.billing.tax.note")}
                                </p>
                            </div>
                        )}

                        {subscription === null && (
                            <CouponField
                                applied={coupon}
                                disabled={checkout.isPending}
                                onCheck={(code) => checkCoupon.mutateAsync(code)}
                                onApplied={setCoupon}
                            />
                        )}

                        <div className="flex flex-wrap gap-3">
                            {subscription === null ? (
                                <Button
                                    onClick={() =>
                                        checkout.mutate({
                                            period,
                                            seats,
                                            coupon: coupon?.code,
                                            modules: chosenModules,
                                            tier,
                                        })
                                    }
                                    disabled={
                                        !state.available ||
                                        checkout.isPending ||
                                        chosen === null ||
                                        tooFew ||
                                        nothingToBuy
                                    }
                                >
                                    {t("center.subscription.buy")}
                                </Button>
                            ) : cancelling ? (
                                <Button onClick={() => resume.mutate()} disabled={resume.isPending}>
                                    {t("subscription.billing.resume")}
                                </Button>
                            ) : (
                                <Button
                                    variant="outline"
                                    onClick={() => setConfirmingCancel(true)}
                                    disabled={cancel.isPending}
                                >
                                    {t("subscription.billing.cancel")}
                                </Button>
                            )}
                            {subscription && (
                                <Button
                                    variant="outline"
                                    onClick={() => portal.mutate()}
                                    disabled={portal.isPending}
                                >
                                    {t("subscription.billing.manage")}
                                </Button>
                            )}
                        </div>

                        {checkout.isError && (
                            <p role="alert" className="text-sm text-red-600 dark:text-red-400">
                                {t(checkoutErrorKey(checkout.error))}
                            </p>
                        )}

                        {!state.available && (
                            <p className="text-xs text-content-tertiary">
                                {t("subscription.billing.unavailable")}
                            </p>
                        )}
                    </section>
                </>
            )}

            <ConfirmDialog
                isOpen={confirmingTier !== null}
                title={t(
                    confirmingTier === "max"
                        ? "center.subscription.tier.confirmMax.title"
                        : "center.subscription.tier.confirmPlus.title"
                )}
                message={t(
                    confirmingTier === "max"
                        ? subscription?.heldTier === "max"
                            ? "center.subscription.tier.confirmMax.messageAlreadyPaid"
                            : "center.subscription.tier.confirmMax.message"
                        : "center.subscription.tier.confirmPlus.message",
                    { date: renewsOn ? day(renewsOn, language) : "", seats: state.seats }
                )}
                confirmLabel={t(
                    confirmingTier === "max"
                        ? "subscription.billing.tier.confirmMax.confirm"
                        : "subscription.billing.tier.confirmPlus.confirm"
                )}
                isBusy={changeTier.isPending}
                onConfirm={() => {
                    if (confirmingTier) changeTier.mutate(confirmingTier);
                    setConfirmingTier(null);
                }}
                onCancel={() => setConfirmingTier(null)}
            />

            {/* What it costs, before it is switched on: the same sentence the
                centre's tools screen says, because it is the same decision. */}
            <ConfirmDialog
                isOpen={adding !== null}
                title={t("center.tools.paidConfirm.title", {
                    tool: adding
                        ? moduleName(t, { slug: adding.slug, name: adding.name })
                        : "",
                })}
                message={t(
                    livePeriod === "annual"
                        ? "center.tools.paidConfirm.messageAnnual"
                        : "center.tools.paidConfirm.message",
                    {
                        price: money(
                            adding ? modulePriceFor(adding, livePeriod) : 0,
                            currency,
                            language
                        ),
                        seats: state.seats,
                        total: money(
                            (adding ? modulePriceFor(adding, livePeriod) : 0) * state.seats,
                            currency,
                            language
                        ),
                    }
                )}
                confirmLabel={t("center.tools.paidConfirm.confirm")}
                isBusy={setPolicy.isPending}
                onConfirm={() => {
                    if (adding?.toolId) {
                        void setPolicy.mutateAsync({
                            toolId: adding.toolId,
                            state: "enabled",
                        });
                    }
                    setAdding(null);
                }}
                onCancel={() => setAdding(null)}
            />

            {/* What the card is about to be charged, before it is. Saying
                "saved" and taking the money was the worst of both.

                With the arithmetic shown: "324,00 € más (324,00 € por
                asiento)" is a figure somebody has to take on trust, and a
                centre with a paid module sees a number that is neither the
                seat's price nor one it recognises. Every line of a centre's
                quote is per seat, so the breakdown is exactly those lines. */}
            <ConfirmDialog
                isOpen={confirmingSeats}
                title={t("center.subscription.seats.payConfirm.title", {
                    count: addingSeats,
                })}
                message={
                    renewsOn
                        ? t("center.subscription.seats.payConfirm.messageWithDate", {
                              count: addingSeats,
                              each: money(perSeatCents, currency, language),
                              added: money(perSeatCents * addingSeats, currency, language),
                              date: day(renewsOn, language),
                          }) + toolsNote
                        : t("center.subscription.seats.payConfirm.message", {
                              count: addingSeats,
                              each: money(perSeatCents, currency, language),
                              added: money(perSeatCents * addingSeats, currency, language),
                          }) + toolsNote
                }
                details={
                    <dl className="space-y-1 rounded-lg bg-gray-50 p-3 text-xs dark:bg-gray-800">
                        {(live?.lines ?? []).map((line) => (
                            <div key={line.key} className="flex justify-between gap-3">
                                <dt className="text-content-secondary">
                                    {lineLabel(t, line)}
                                </dt>
                                <dd className="font-medium text-content-primary">
                                    {money(line.unitCents, currency, language)}
                                </dd>
                            </div>
                        ))}
                        <div className="flex justify-between gap-3 border-t border-gray-200 pt-1 dark:border-gray-700">
                            <dt className="text-content-secondary">
                                {t("center.subscription.seats.payConfirm.perSeat")}
                            </dt>
                            <dd className="font-medium text-content-primary">
                                {money(perSeatCents, currency, language)}
                            </dd>
                        </div>
                        <div className="flex justify-between gap-3">
                            <dt className="text-content-secondary">
                                {t("center.subscription.seats.payConfirm.newSeats", {
                                    count: addingSeats,
                                })}
                            </dt>
                            <dd className="font-semibold text-content-primary">
                                {money(perSeatCents * addingSeats, currency, language)}
                            </dd>
                        </div>
                    </dl>
                }
                confirmLabel={t("center.subscription.seats.payConfirm.confirm")}
                isBusy={setSeats.isPending}
                onConfirm={() => {
                    setSeats.mutate(seats);
                    setConfirmingSeats(false);
                }}
                onCancel={() => setConfirmingSeats(false)}
            />

            {/* Why, as a therapist is asked — both optional. */}
            <CancelSubscriptionDialog
                isOpen={confirmingCancel}
                title={t("center.subscription.cancelConfirm.title")}
                message={t("center.subscription.cancelConfirm.message")}
                isBusy={cancel.isPending}
                onConfirm={(why) => {
                    cancel.mutate(why);
                    setConfirmingCancel(false);
                }}
                onClose={() => setConfirmingCancel(false)}
            />
        </div>
    );

    return bought && center ? (
        <CenterAdminShell center={center}>{body}</CenterAdminShell>
    ) : (
        body
    );
}
